import compact from "lodash/compact";
import map from "lodash/map";
import { convertFontTagFromFormat } from "../../../agreed/fonts";
import { upgradeTemplateToV2 } from "../../../agreed/formats/v1/migrations";
import { TemplateFormatV1 } from "../../../agreed/formats/v1/template_format";
import { upgradeTemplateToV3 } from "../../../agreed/formats/v2/migrations";
import { TemplateFormatV2 } from "../../../agreed/formats/v2/template_format";
import { upgradeTemplateToV4 } from "../../../agreed/formats/v3/migrations";
import { TemplateFormatV3 } from "../../../agreed/formats/v3/template_format";
import { upgradeTemplateToV5 } from "../../../agreed/formats/v4/migrations";
import { TemplateFormatV4 } from "../../../agreed/formats/v4/template_format";
import { upgradeTemplateToV6 } from "../../../agreed/formats/v5/migrations";
import { TemplateFormatV5 } from "../../../agreed/formats/v5/template_format";
import {
  AnchorFormatV6,
  BoxableFormatV6,
  LocalizedContentFormatV6,
  PageDefinition,
  TemplateConditionFormatV6,
  TemplateFormatV6,
  TextableFormatV6,
  TextAlignFormatV6,
} from "../../../agreed/formats/v6/template_format";
import {
  Anchor,
  AnchorHorizontal,
  AnchorVertical,
  Boxable,
  Unit,
  UnsupportedAnchorError,
} from "../../../types/customization/box";
import {
  Template,
  TemplateCondition,
  TemplateForcedTextCommonDefinition,
  TemplateForcedTextForcedContentDefinition,
  TemplateForcedTextInterpolatedContentDefinition,
} from "../../../types/customization/template";
import {
  LocalizedContent,
  Textable,
  TextAlign,
  TextAlignHorizontal,
  TextAlignVertical,
  TextOrientation,
  UnsupportedTextAlignError,
  UnsupportedTextOrientationError,
} from "../../../types/customization/text";

export function convertFromTemplateFormat(
  templateFormat:
    | TemplateFormatV1
    | TemplateFormatV2
    | TemplateFormatV3
    | TemplateFormatV4
    | TemplateFormatV5
    | TemplateFormatV6
): Template {
  if (templateFormat.definition_version === 6) {
    const templateCommon = {
      definitionVersion: templateFormat.definition_version,
      minDpi: templateFormat.min_dpi,

      assetDefinitions: map(templateFormat.asset_definitions, (definition) => ({
        key: definition.key,

        assetId: definition.asset_id,
      })),

      colorDefinitions: templateFormat.color_definitions.map((colorDefinition) => {
        return {
          fontColors: colorDefinition.font_colors,
          key: colorDefinition.key,
          value: colorDefinition.value,
        };
      }),

      contentPageConfiguration: {
        forcePackColor: templateFormat.content_page_configuration.force_pack_color,
        forcedLayouts: templateFormat.content_page_configuration.forced_layouts.map(
          (forcedLayout) => {
            return { filterType: forcedLayout.filter_type };
          }
        ),
        addNext: { filterType: templateFormat.content_page_configuration.add_next.filter_type },
      },

      fontDefinitions: map(templateFormat.font_definitions, (definition) => ({
        key: definition.key,

        fontTag: convertFontTagFromFormat(definition.font_tag),
        widthPerHeightMeanCharRatio: definition.width_per_height_mean_char_ratio,
      })),

      optionDefinitions: compact(
        map(templateFormat.option_definitions, (definition) => {
          switch (definition.type) {
            case "customization":
              return {
                iconAssetKey: definition.icon_asset_key,
                key: definition.key,
                type: definition.type,

                onlyGlobal: definition.only_global,
                optionChoices: definition.option_choices.map((optionChoice) => {
                  return {
                    assetKey: optionChoice.asset_key,
                    label: optionChoice.label,
                    value: optionChoice.value,
                  };
                }),
                optionTag: definition.option_tag,
                optionTitle: definition.option_title,
              };
          }
        })
      ),

      pageDefinitions: map(
        filterPageDefinitionsWithoutExtraType(templateFormat.page_definitions),
        (definition) => ({
          filterType: definition.filter_type,
          key: definition.key,
          type: definition.type,

          rotationFromPageDefinitionKey: definition.rotation_from_page_definition_key,

          height: {
            value: convertDmmToMm(definition.height_dmm),
            unit: Unit.MM,
          } as const,
          width: {
            value: convertDmmToMm(definition.width_dmm),
            unit: Unit.MM,
          } as const,

          shapeKey: definition.shape_key,

          conditions: definition.conditions,

          backgroundColorKeys: definition.background_color_keys,

          elementDefinitions: map(definition.elements, (element) => {
            switch (element.type) {
              case "editable-picture":
                return {
                  key: element.key,
                  type: element.type,
                  ...convertBoxableFromFormat(element, definition.width_dmm, definition.height_dmm),

                  apparentShapeKey: element.apparent_shape_key,
                  shapeKey: element.shape_key,

                  conditions: element.conditions,
                };
              case "editable-text":
                return {
                  key: element.key,
                  type: element.type,
                  ...convertBoxableFromFormat(element, definition.width_dmm, definition.height_dmm),
                  ...convertTextableFromFormat(element, definition.height_dmm),

                  placeholder: map(element.placeholder, convertLocalizedContentFromFormat),

                  conditions: element.conditions,
                };
              case "forced-image":
                return {
                  key: element.key,
                  type: element.type,
                  ...convertBoxableFromFormat(element, definition.width_dmm, definition.height_dmm),

                  assetKey: element.asset_key,

                  conditions: element.conditions,
                };
              case "forced-text": {
                const forcedText: TemplateForcedTextCommonDefinition = {
                  key: element.key,
                  type: element.type,
                  ...convertBoxableFromFormat(element, definition.width_dmm, definition.height_dmm),
                  ...convertTextableFromFormat(element, definition.height_dmm),

                  fontKey: element.font_key,

                  conditions: element.conditions,
                };
                if (element.forced_content && typeof element.forced_content !== "string") {
                  const forcedContent = map(
                    element.forced_content,
                    convertLocalizedContentFromFormat
                  );
                  const forcedTextForcedContent: TemplateForcedTextForcedContentDefinition = {
                    ...forcedText,
                    forcedContent,
                    forcedType: "forced-content",
                  };
                  return forcedTextForcedContent;
                } else if (element.interpolated_content) {
                  const interpolatedContent = element.interpolated_content;
                  const forcedTextInterpolatedContent: TemplateForcedTextInterpolatedContentDefinition = {
                    ...forcedText,
                    forcedType: "interpolated-content",
                    interpolatedContent,
                  };
                  return forcedTextInterpolatedContent;
                } else {
                  throw new Error(`Unsupported forcedText content`);
                }
              }
              default: {
                const exhaustiveCheck: never = element;
                throw new Error(`Unsupported type "${(exhaustiveCheck as { type: string }).type}"`);
              }
            }
          }),

          presentations: map(definition.presentations, (presentation) => ({
            presentationDefinitionKey: presentation.presentation_definition_key,

            conditions: presentation.conditions
              ? convertTemplateConditionFromFormat(presentation.conditions)
              : undefined,
          })),
        })
      ),

      presentationDefinitions: map(templateFormat.presentation_definitions, (definition) => ({
        key: definition.key,

        assetKey: definition.asset_key,

        height: {
          value: convertDmmToMm(definition.height_dmm),
          unit: Unit.MM,
        } as const,
        width: {
          value: convertDmmToMm(definition.width_dmm),
          unit: Unit.MM,
        } as const,

        contentX: {
          unit: Unit.MM,
          value: convertDmmToMm(definition.content_x_dmm),
        } as const,
        contentY: {
          unit: Unit.MM,
          value: convertDmmToMm(definition.content_y_dmm),
        } as const,

        above: definition.above,
      })),

      shapeDefinitions: map(templateFormat.shape_definitions, (definition) => ({
        key: definition.key,

        ratio: definition.ratio,
        svgContent: definition.svg_content,
      })),
    };
    if (templateFormat.template_type === "book") {
      return {
        pageMode: templateFormat.page_mode,
        templateType: templateFormat.template_type,
        ...templateCommon,
      };
    } else if (templateFormat.template_type === "mural") {
      return {
        templateType: templateFormat.template_type,
        ...templateCommon,
      };
    } else if (templateFormat.template_type === "print") {
      return {
        templateType: templateFormat.template_type,
        ...templateCommon,
      };
    } else {
      throw new Error(
        `Unsupported templateType "${(templateFormat as { template_type: string }).template_type}"`
      );
    }
  } else if (templateFormat.definition_version === 5) {
    return convertFromTemplateFormat(upgradeTemplateToV6(templateFormat));
  } else if (templateFormat.definition_version === 4) {
    return convertFromTemplateFormat(upgradeTemplateToV5(templateFormat));
  } else if (templateFormat.definition_version === 3) {
    return convertFromTemplateFormat(upgradeTemplateToV4(templateFormat));
  } else if (templateFormat.definition_version === 2) {
    return convertFromTemplateFormat(upgradeTemplateToV3(templateFormat));
  } else if (templateFormat.definition_version === 1) {
    return convertFromTemplateFormat(upgradeTemplateToV2(templateFormat));
  } else {
    throw new Error(
      `Unsupported definition_version "${
        (templateFormat as { definition_version: number }).definition_version
      }"`
    );
  }
}

function convertTemplateConditionFromFormat(
  templateConditionsFormat: TemplateConditionFormatV6[]
): TemplateCondition[] {
  return templateConditionsFormat.map((templateCondition) => {
    return {
      optionDefinitionKey: templateCondition.option_definition_key,
      type: templateCondition.type,
      value: templateCondition.value,
    };
  });
}

function convertLocalizedContentFromFormat(
  localizedContent: LocalizedContentFormatV6
): LocalizedContent {
  return {
    content: localizedContent.content,
    locale: localizedContent.locale,
  };
}

function convertBoxableFromFormat(
  element: BoxableFormatV6,
  referenceWidthDmm: number,
  referenceHeightDmm: number
): Boxable {
  return {
    height: {
      unit: Unit.P10K,
      value: convertDmmToP10K(element.height_dmm, referenceHeightDmm),
    } as const,
    width: {
      unit: Unit.P10K,
      value: convertDmmToP10K(element.width_dmm, referenceWidthDmm),
    } as const,

    anchorPage: convertAnchorFromFormat(element.anchor_page),
    anchorSelf: convertAnchorFromFormat(element.anchor_self),
    x: {
      value: convertDmmToP10K(element.x_dmm, referenceWidthDmm),
      unit: Unit.P10K,
    } as const,
    y: {
      value: convertDmmToP10K(element.y_dmm, referenceHeightDmm),
      unit: Unit.P10K,
    } as const,

    zIndex: element.z_index,
  };
}

function convertTextableFromFormat(
  element: TextableFormatV6,
  referenceHeightDmm: number
): Textable {
  return {
    fontColorKey: element.font_color_key,

    fontSize: {
      unit: Unit.P10K,
      value: convertDmmToP10K(element.font_size_dmm, referenceHeightDmm),
    } as const,
    lineHeight: {
      unit: Unit.P10K,
      value: convertDmmToP10K(element.line_height_dmm, referenceHeightDmm),
    } as const,

    textAlign: convertTextAlignFromFormat(element.text_align),
    textOrientation: convertTextOrientationFromFormat(element.text_orientation),
  };
}

function convertAnchorFromFormat(anchor: AnchorFormatV6): Anchor {
  let anchorVertical;
  let anchorHorizontal;

  switch (anchor[0]) {
    case "top":
      anchorVertical = AnchorVertical.TOP;
      break;
    case "middle":
      anchorVertical = AnchorVertical.MIDDLE;
      break;
    case "bottom":
      anchorVertical = AnchorVertical.BOTTOM;
      break;
    default: {
      const exhaustiveCheck: never = anchor[0];
      throw new UnsupportedAnchorError(exhaustiveCheck);
    }
  }

  switch (anchor[1]) {
    case "left":
      anchorHorizontal = AnchorHorizontal.LEFT;
      break;
    case "center":
      anchorHorizontal = AnchorHorizontal.CENTER;
      break;
    case "right":
      anchorHorizontal = AnchorHorizontal.RIGHT;
      break;
    default: {
      const exhaustiveCheck: never = anchor[1];
      throw new UnsupportedAnchorError(exhaustiveCheck);
    }
  }

  return [anchorVertical, anchorHorizontal];
}

function convertTextAlignFromFormat(textAlign: TextAlignFormatV6): TextAlign {
  let textAlignVertical;
  let textAlignHorizontal;

  switch (textAlign[0]) {
    case "top":
      textAlignVertical = TextAlignVertical.TOP;
      break;
    case "middle":
      textAlignVertical = TextAlignVertical.MIDDLE;
      break;
    case "bottom":
      textAlignVertical = TextAlignVertical.BOTTOM;
      break;
    default:
      throw new UnsupportedTextAlignError(textAlign[0]);
  }

  switch (textAlign[1]) {
    case "left":
      textAlignHorizontal = TextAlignHorizontal.LEFT;
      break;
    case "center":
      textAlignHorizontal = TextAlignHorizontal.CENTER;
      break;
    case "right":
      textAlignHorizontal = TextAlignHorizontal.RIGHT;
      break;
    default:
      throw new UnsupportedTextAlignError(textAlign[1]);
  }

  return [textAlignVertical, textAlignHorizontal];
}

function convertTextOrientationFromFormat(orientation: 0 | 90 | 180 | 270): TextOrientation {
  switch (orientation) {
    case 0:
      return TextOrientation.O000;
    case 90:
      return TextOrientation.O090;
    case 180:
      return TextOrientation.O180;
    case 270:
      return TextOrientation.O270;
    default:
      throw new UnsupportedTextOrientationError(orientation);
  }
}

export function filterPageDefinitionsWithoutExtraType(
  pageDefinitions: PageDefinition[]
): PageDefinition[] {
  return pageDefinitions.filter((definition) => definition.type !== "extra");
}

function convertDmmToMm(value: number): number {
  return value / 10;
}

function convertDmmToP10K(sizeDmm: number, referenceDmm: number): number {
  return Math.round((sizeDmm / referenceDmm) * 10000);
}
